import { useQuery } from '@apollo/client';
import React, { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Icon,
  SimpleTooltip,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useIsEluveAdmin } from '@eluve/session-helpers';

const getTenantSyncedEhrIdsQuery = graphql(`
  query getTenantSyncedEhrIds($tenantId: uuid!) {
    tenantsByPk(id: $tenantId) {
      __typename
      id
      locations(
        distinctOn: externalEhrId
        where: {
          externalEhrId: { _isNull: false }
          externalId: { _isNull: false }
        }
      ) {
        __typename
        id
        externalEhrId
      }
    }
  }
`);

/**
 * Queues a backend job that re-attributes all users and the last 60 days of
 * appointments from EMR sync data. Eluve admins only (also enforced by the
 * backend); hidden when the tenant has no EMR-synced locations. The backend
 * enforces a single queued/running job per EHR.
 */
export const BackfillEmrDataButton: React.FC<{ tenantId: string }> = ({
  tenantId,
}) => {
  const apiClient = useApiClient();
  const toast = useToast();
  const isEluveAdmin = useIsEluveAdmin();
  const [isQueueing, setIsQueueing] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const { data } = useQuery(getTenantSyncedEhrIdsQuery, {
    variables: { tenantId },
    skip: !isEluveAdmin,
  });

  const ehrIds = (data?.tenantsByPk?.locations ?? []).flatMap((location) =>
    location.externalEhrId ? [location.externalEhrId] : [],
  );

  if (!isEluveAdmin || ehrIds.length === 0) {
    return null;
  }

  const queueBackfill = async () => {
    setIsConfirmOpen(false);
    setIsQueueing(true);
    try {
      const responses = await Promise.all(
        ehrIds.map((externalEhrId) =>
          apiClient.locations.backfillLocationAttribution({
            params: { tenantId },
            body: { externalEhrId },
          }),
        ),
      );

      if (responses.every((r) => r.status === 201)) {
        toast.success('Backfill queued — data will update shortly');
      } else if (responses.some((r) => r.status === 409)) {
        toast.info('A backfill is already queued or running');
      } else {
        toast.error('Failed to queue backfill');
      }
    } catch {
      toast.error('Failed to queue backfill');
    } finally {
      setIsQueueing(false);
    }
  };

  return (
    <>
      <SimpleTooltip label="Re-attributes all users and the last 60 days of appointments from EMR sync data">
        <Button
          variant="outline"
          disabled={isQueueing}
          onClick={() => setIsConfirmOpen(true)}
        >
          <Icon name="RefreshCw" size="xs" className="mr-2" />
          Backfill EMR data
        </Button>
      </SimpleTooltip>
      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Backfill EMR data?</AlertDialogTitle>
            <AlertDialogDescription>
              This re-attributes all users and the last 60 days of appointments
              from EMR sync data. It is a resource-intensive job — run it only
              when location data is missing or out of date.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={queueBackfill} disabled={isQueueing}>
              Backfill
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
